import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import Icon from '../../../components/AppIcon';

import { QRAnalyticsService } from '../../../services/qrAnalyticsService';

const ScanTrendsChart = ({ 
  data = [],
  isLoading = false,
  onDataPointClick,
  selectedDateRange = 'last7d'
}) => {
  const [chartType, setChartType] = useState('line');
  const [selectedMetric, setSelectedMetric] = useState('scans');
  const [localData, setLocalData] = useState([]);
  const [localLoading, setLocalLoading] = useState(false);
  const [error, setError] = useState('');

  // Load chart data when component mounts or date range changes
  useEffect(() => {
    const loadChartData = async () => {
      // If data is provided via props, use it
      if (data && data?.length > 0) {
        setLocalData(data);
        return;
      }

      // Otherwise, fetch data from service
      setLocalLoading(true);
      setError('');

      try {
        const { data: trendsData, error: trendsError } = await QRAnalyticsService?.getScanTrends(selectedDateRange);
        
        if (trendsError) {
          setError(trendsError);
          setLocalData([]); // No fallback data - show empty state
        } else {
          setLocalData(trendsData || []);
        }
      } catch (error) {
        setError(`Failed to load chart data: ${error?.message}`);
        setLocalData([]); // No fallback data - show empty state
      } finally {
        setLocalLoading(false);
      }
    };

    loadChartData();
  }, [data, selectedDateRange]);

  const chartData = localData || [];
  const showLoading = isLoading || localLoading;

  const metrics = [
    { key: 'scans', label: 'Total Scans', color: '#3B82F6' },
    { key: 'uniqueScans', label: 'Unique Scans', color: '#10B981' },
    { key: 'conversions', label: 'Conversions', color: '#F59E0B' }
  ];

  const currentMetric = metrics?.find(m => m?.key === selectedMetric) || metrics?.[0];

  const handleDataPointClick = (data) => {
    if (onDataPointClick) {
      onDataPointClick(data);
    } else {
      console.log('Chart data point clicked:', data);
    }
  };

  // Calculate summary statistics
  const totalScans = chartData?.reduce((sum, item) => sum + (item?.scans || 0), 0);
  const avgScans = chartData?.length > 0 ? Math.round(totalScans / chartData?.length) : 0;
  const maxScans = chartData?.length > 0 ? Math.max(...chartData?.map(item => item?.scans || 0)) : 0;

  if (showLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Scan Trends</h3>
            <p className="text-sm text-gray-500 mt-1">Loading chart data...</p>
          </div>
          <div className="animate-spin">
            <Icon name="Loader2" size={24} className="text-gray-400" />
          </div>
        </div>
        
        <div className="h-80 bg-gray-50 rounded-lg flex items-center justify-center">
          <div className="text-center">
            <Icon name="BarChart3" size={48} className="text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Loading chart data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Scan Trends</h3>
          <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
            <span>Total: {totalScans?.toLocaleString()}</span>
            <span>Avg: {avgScans?.toLocaleString()}/day</span>
            <span>Peak: {maxScans?.toLocaleString()}</span>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-2 mt-4 lg:mt-0">
          {/* Metric Selector */}
          <select
            value={selectedMetric}
            onChange={(e) => setSelectedMetric(e?.target?.value)}
            className="text-sm border border-gray-200 rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {metrics?.map((metric) => (
              <option key={metric?.key} value={metric?.key}>
                {metric?.label}
              </option>
            ))}
          </select>

          {/* Chart Type Toggle */}
          <div className="flex bg-gray-100 rounded-md p-1">
            <button
              onClick={() => setChartType('line')}
              className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                chartType === 'line' ?'bg-white text-gray-900 shadow-sm' :'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Icon name="TrendingUp" size={14} />
            </button>
            <button
              onClick={() => setChartType('area')}
              className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                chartType === 'area' ?'bg-white text-gray-900 shadow-sm' :'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Icon name="BarChart3" size={14} />
            </button>
          </div>
        </div>
      </div>
      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <div className="flex items-start">
            <Icon name="AlertTriangle" size={16} className="text-yellow-600 mt-0.5 mr-2" />
            <div>
              <p className="text-sm text-yellow-800 font-medium">Data Loading Issue</p>
              <p className="text-xs text-yellow-700 mt-1">{error} - Showing preview data</p>
            </div>
          </div>
        </div>
      )}
      {/* Chart */}
      <div className="h-80">
        {chartData?.length === 0 ? (
          <div className="h-full bg-gray-50 rounded-lg flex items-center justify-center">
            <div className="text-center">
              <Icon name="BarChart3" size={48} className="text-gray-300 mx-auto mb-4" />
              <h4 className="text-lg font-medium text-gray-600 mb-2">No Data Available</h4>
              <p className="text-gray-500">No scan data found for the selected period.</p>
            </div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            {chartType === 'area' ? (
              <AreaChart
                data={chartData}
                margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
                onClick={handleDataPointClick}
              >
                <defs>
                  <linearGradient id={`color-${selectedMetric}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={currentMetric?.color} stopOpacity={0.3}/>
                    <stop offset="95%" stopColor={currentMetric?.color} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                <XAxis 
                  dataKey="date" 
                  stroke="#9CA3AF" 
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis 
                  stroke="#9CA3AF" 
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                  }}
                />
                <Area
                  type="monotone"
                  dataKey={selectedMetric}
                  stroke={currentMetric?.color}
                  strokeWidth={2}
                  fill={`url(#color-${selectedMetric})`}
                />
              </AreaChart>
            ) : (
              <LineChart
                data={chartData}
                margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
                onClick={handleDataPointClick}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                <XAxis 
                  dataKey="date" 
                  stroke="#9CA3AF" 
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis 
                  stroke="#9CA3AF" 
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                  }}
                />
                <Line
                  type="monotone"
                  dataKey={selectedMetric}
                  stroke={currentMetric?.color}
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6, stroke: currentMetric?.color }}
                />
              </LineChart>
            )}
          </ResponsiveContainer>
        )}
      </div>
      {/* Legend */}
      <div className="flex flex-wrap items-center justify-center gap-4 mt-4 pt-4 border-t border-gray-100">
        {metrics?.map((metric) => (
          <button
            key={metric?.key}
            onClick={() => setSelectedMetric(metric?.key)}
            className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium transition-colors ${
              selectedMetric === metric?.key
                ? 'bg-gray-100 text-gray-900' :'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <div 
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: metric?.color }}
            />
            {metric?.label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default ScanTrendsChart;